import React, { useEffect, useState, useContext, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { getPokemonSpriteUrl, normalizePokemonName, getPokemonIdFromName } from '../../api/pokeApi';
import { getBaseSpeciesName } from '../../utils/pokemonUtils';
import UnifiedMatchContext from '../../context/matches/UnifiedMatchContext';
import EditNotesModal from './EditNotesModal';
import ConfirmModal from '../layout/ConfirmModal';
import PokemonSprite from '../shared/PokemonSprite';
import '../../styles/matches.css';
import './MatchItem.css';
import './LogMatch.css';
import './MatchItemMobile.css';
import './MatchItemModern.css';

const MatchItemVGC = ({ match, deleteMatch = null, useModernUI = true }) => {
  
  const {
    _id,
    playerTeam = [],
    opponentTeam = [],
    opponentDeck,
    opponentName,
    notes,
    createdAt,
    date,
    gameType,
    games = [],
    format,
    bestOf = 1,
    matchScore,
    replayLink,
    group,
    restrictedPokemon = []
  } = match;

  const { deleteVGCMatch, updateVGCMatch } = useContext(UnifiedMatchContext);
  const [showEditNotesModal, setShowEditNotesModal] = useState(false);
  const [editingGameIndex, setEditingGameIndex] = useState(null);
  const [showGameDetailsModal, setShowGameDetailsModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [playerSprites, setPlayerSprites] = useState([]);
  const [opponentSprites, setOpponentSprites] = useState([]);
  const [expandedGameIndex, setExpandedGameIndex] = useState(null);
  const [gameLeadSprites, setGameLeadSprites] = useState({});
  const [gameOpponentLeadSprites, setGameOpponentLeadSprites] = useState({});
  const [gameTeamsMap, setGameTeamsMap] = useState({});

  const wins = games?.filter(g => g.result === 'win')?.length || 0;
  const losses = games?.filter(g => g.result === 'loss')?.length || 0;

  const score = match.matchScore || `${wins}-${losses}`;
  const matchResult = {
    result: wins > losses ? 'Won' : 'Lost',
    score: score,
    status: wins > losses ? 'success' : 'danger'
  };

  // Helper function to check if a Pokémon is restricted
  const isRestricted = (pokemonName, team) => {
    return restrictedPokemon.some(
      p => p.name.toLowerCase() === pokemonName.toLowerCase() && p.team === team
    );
  };

  const parsedPlayerTeam = useMemo(() => {
    // Better handling of player team to extract unique Pokémon names
    if (Array.isArray(playerTeam)) {
      return playerTeam;
    } else if (typeof playerTeam === 'string') {
      // Extract unique Pokémon from the comma-separated string
      const pokemonList = playerTeam.split(',').map(p => p.trim()).filter(p => p);
      
      // Use a Map to deduplicate by base species but keep the most specific form
      const uniquePokemon = new Map();
      pokemonList.forEach(pokemon => {
        const baseSpecies = getBaseSpeciesName(pokemon);
        // Keep the most specific form (e.g., keep Zamazenta-Crowned over Zamazenta)
        if (!uniquePokemon.has(baseSpecies) || pokemon.includes('-')) {
          uniquePokemon.set(baseSpecies, pokemon);
        }
      });
      
      return Array.from(uniquePokemon.values());
    }
    return [];
  }, [playerTeam]);

  const parsedOpponentTeam = useMemo(() => {
    // Better handling of opponent team with unique Pokémon
    if (Array.isArray(opponentTeam)) {
      return opponentTeam;
    } else if (typeof opponentTeam === 'string') {
      const pokemonList = opponentTeam.includes(',') 
        ? opponentTeam.split(',').map(p => p.trim()).filter(p => p)
        : [opponentTeam.trim()].filter(p => p);
      
      // Use a Map to deduplicate by base species but keep the most specific form
      const uniquePokemon = new Map();
      pokemonList.forEach(pokemon => {
        const baseSpecies = getBaseSpeciesName(pokemon);
        // Keep the most specific form (e.g., keep Zamazenta-Crowned over Zamazenta)
        if (!uniquePokemon.has(baseSpecies) || pokemon.includes('-')) {
          uniquePokemon.set(baseSpecies, pokemon);
        }
      });
      
      return Array.from(uniquePokemon.values());
    }
    return [];
  }, [opponentTeam]);

  useEffect(() => {
    const loadSprites = async () => {
      // We'll use the global sprite cache service imported from pokeApi
      // This ensures we don't make redundant API calls across the app
      
      // Simplified sprite resolution function that uses the cache service
      const resolveSprite = async (pokemon) => {
        if (!pokemon) return 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/25.png';
        
        let name = typeof pokemon === 'string' ? pokemon : pokemon?.slug || pokemon?.name || '';
        
        try {
          return await getPokemonSpriteUrl(name);
        } catch {
          return 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/25.png';
        }
      };
      
      // Process an array of Pokémon names and return their sprites
      const resolveSprites = async (names) => {
        if (!Array.isArray(names)) return [];
        return Promise.all(names.map(resolveSprite));
      };
      
      // Collect all unique Pokémon that need sprites
      const allPokemon = new Set();
      
      // Add all pokemon from teams
      parsedPlayerTeam.forEach(pokemon => allPokemon.add(pokemon));
      parsedOpponentTeam.forEach(pokemon => allPokemon.add(pokemon));
      
      // Add Pokémon from each game
      games.forEach(game => {
        // Player leads and brought Pokémon
        if (game.leadPokemon) {
          game.leadPokemon.forEach(pokemon => allPokemon.add(pokemon));
        }
        if (game.broughtPokemon) {
          game.broughtPokemon.forEach(pokemon => allPokemon.add(pokemon));
        }
        
        // Opponent leads
        if (game.opponentLeadPokemon) {
          game.opponentLeadPokemon.forEach(pokemon => allPokemon.add(pokemon));
        }
      });
      
      // Pre-cache all sprites upfront in parallel
      await Promise.all([...allPokemon].map(resolveSprite));
      
      // Now set sprites with cached values (these will be fast since they're cached)
      setPlayerSprites(await resolveSprites(parsedPlayerTeam));
      setOpponentSprites(await resolveSprites(parsedOpponentTeam));
      
      // Prepare data structures for games
      const leadSpritesMap = {};
      const opponentLeadSpritesMap = {};
      const gameTeamsMap = {};
      
      // Process each game in sequence
      for (let i = 0; i < games.length; i++) {
        const game = games[i];
        
        // Get lead Pokémon sprites (using cached values)
        leadSpritesMap[i] = await resolveSprites(game.leadPokemon || []);
        opponentLeadSpritesMap[i] = await resolveSprites(game.opponentLeadPokemon || []);
        
        // Prepare game teams container
        gameTeamsMap[i] = {};
        
        // For player team
        // 1. First try to use broughtPokemon from the game data
        // 2. If not available, fall back to leadPokemon
        // 3. If that's also not available, use the match-level player team
        let playerTeamData = [];
        
        if (game.broughtPokemon && game.broughtPokemon.length > 0) {
          playerTeamData = [...game.broughtPokemon];
        } else if (game.leadPokemon && game.leadPokemon.length > 0) {
          playerTeamData = [...game.leadPokemon];
          
          // Add other Pokémon from the full player team to make up at least 4 if possible
          if (parsedPlayerTeam.length > 0) {
            for (const pokemon of parsedPlayerTeam) {
              if (!playerTeamData.includes(pokemon) && playerTeamData.length < 4) {
                playerTeamData.push(pokemon);
              }
            }
          }
        } else {
          // Fallback to the first 4 Pokémon from the match-level player team
          playerTeamData = parsedPlayerTeam.slice(0, 4);
        }
        
        // For opponent team
        // 1. First use opponentLeadPokemon from the game
        // 2. Add more from the match-level opponent team if needed
        let opponentTeamData = [];
        
        if (game.opponentLeadPokemon && game.opponentLeadPokemon.length > 0) {
          opponentTeamData = [...game.opponentLeadPokemon];
          
          // Add other Pokémon from the full opponent team to make up at least 4 if possible
          if (parsedOpponentTeam.length > 0) {
            for (const pokemon of parsedOpponentTeam) {
              if (!opponentTeamData.includes(pokemon) && opponentTeamData.length < 4) {
                opponentTeamData.push(pokemon);
              }
            }
          }
        } else {
          // Fallback to the first 4 Pokémon from the match-level opponent team
          opponentTeamData = parsedOpponentTeam.slice(0, 4);
        }
        
        // Get sprites for both teams (using cached values so this is fast)
        gameTeamsMap[i].playerSprites = await resolveSprites(playerTeamData);
        gameTeamsMap[i].opponentSprites = await resolveSprites(opponentTeamData);
        
        // Store the actual Pokémon names to ensure correct sprite-to-name mapping
        gameTeamsMap[i].playerTeamData = playerTeamData;
        gameTeamsMap[i].opponentTeamData = opponentTeamData;
      }

      setGameLeadSprites(leadSpritesMap);
      setGameOpponentLeadSprites(opponentLeadSpritesMap);
      setGameTeamsMap(gameTeamsMap);
    };

    loadSprites();
  }, [playerTeam, opponentTeam, games, parsedPlayerTeam, parsedOpponentTeam]);

  const handleDelete = async (id) => {
    // For VGC matches, we should always use deleteVGCMatch
    // Since this is MatchItemVGC, we'll prioritize the VGC delete function
    if (deleteVGCMatch) {
      await deleteVGCMatch(id);
    } else if (deleteMatch) {
      deleteMatch(id);
    }
    setShowDeleteConfirm(false);
  };
  
  const handleEditGameNotes = (e, gameIndex) => {
    // Stop propagation to prevent any parent handlers from firing
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setEditingGameIndex(gameIndex);
    setShowEditNotesModal(true);
  };
  
  const handleSaveGameNotes = async (updatedNotes) => {
    try {
      if (editingGameIndex !== null && editingGameIndex >= 0 && editingGameIndex < games.length) {
        // Create a copy of the games array
        const updatedGames = [...games];
        
        // Update the notes for the specific game
        updatedGames[editingGameIndex] = {
          ...updatedGames[editingGameIndex],
          notes: updatedNotes
        };
        
        // Create a copy of the match with updated games
        const updatedMatch = {
          ...match,
          games: updatedGames
        };
        
        // Update the match in the database
        await updateVGCMatch(updatedMatch);
      }
    } catch (error) {
      console.error('Error updating game notes:', error);
      alert('Failed to save game notes. Please try again.');
      return; // Don't close the modal if save failed
    }
    
    // Only close the modal if save succeeded
    setEditingGameIndex(null);
    setShowEditNotesModal(false);
  };
  
  const handleEditNotes = (e) => {
    // Stop propagation to prevent any parent handlers from firing
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setEditingGameIndex(null); // Not editing a specific game
    setShowEditNotesModal(true);
  };
  
  const handleSaveNotes = async (updatedNotes) => {
    try {
      // Create a copy of the match with updated notes
      const updatedMatch = {
        ...match,
        notes: updatedNotes
      };
      
      // Update the match in the database
      await updateVGCMatch(updatedMatch);
    } catch (error) {
      console.error('Error updating match notes:', error);
      alert('Failed to save match notes. Please try again.');
      return; // Don't close the modal if save failed
    }
    
    // Only close the modal if save succeeded
    setShowEditNotesModal(false);
  };

  const handleGameClick = (e, game, index) => {
    // Stop propagation to prevent expanding/collapsing the match item
    e.stopPropagation();
    
    // Set the selected game and show the modal
    setSelectedGame({...game, gameNumber: index + 1});
    setShowGameDetailsModal(true);
  };

  const closeGameDetailsModal = () => {
    setShowGameDetailsModal(false);
    setSelectedGame(null);
  };
  
  const formatDate = (dateValue) => {
    if (!dateValue) return 'No date available';
    
    try {
      const date = new Date(dateValue);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Date error';
    }
  };

  if (useModernUI) {
    return (
      <div className={`match-item-modern`} onClick={() => {
        if (games && games.length > 0) {
          setSelectedGame({...games[0], gameNumber: 1});
        } else {
          setSelectedGame({ gameNumber: 1 });
        }
        setShowGameDetailsModal(true);
      }}>
        {/* Match Header */}
        <div className="match-header-modern">
          <span className={`match-result-badge ${matchResult.result.toLowerCase()}`}>
            {matchResult.result} {score}
          </span>
          <span className="match-format-badge">
            VGC • Doubles
          </span>
          {group && (
            <span className="match-format-badge">
              <i className="fas fa-object-group"></i> {group.name}
            </span>
          )}
        </div>

        {/* Match Meta Info */}
        <div className="match-meta-info">
          <div className="match-meta-item">
            <i className="fas fa-user"></i>
            <span>vs {opponentName || opponentDeck || 'Unknown'}</span>
          </div>
          <div className="match-meta-item">
            <i className="fas fa-gamepad"></i>
            <span>{format || `Best of ${bestOf}`}</span>
          </div>
        </div>

        {/* VGC Teams Visual */}
        <div className="vgc-teams-container">
          <div className="vgc-team-column">
            <span className="vgc-team-label">You</span>
            <div className="vgc-team-sprites">
              {playerSprites.slice(0, 6).map((url, i) => (
                <div key={`ps-${i}`} className="vgc-pokemon-sprite" style={{ position: 'relative' }}>
                  <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  {isRestricted(parsedPlayerTeam[i], 'player') && (
                    <span className="restricted-badge" title="Restricted Pokémon">
                      <i className="fas fa-crown"></i>
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="vs-separator-container">
            <span className="vs-separator">VS</span>
          </div>
          <div className="vgc-team-column">
            <span className="vgc-team-label">Opp</span>
            <div className="vgc-team-sprites">
              {opponentSprites.slice(0, 6).map((url, i) => (
                <div key={`os-${i}`} className="vgc-pokemon-sprite" style={{ position: 'relative' }}>
                  <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  {isRestricted(parsedOpponentTeam[i], 'opponent') && (
                    <span className="restricted-badge" title="Restricted Pokémon">
                      <i className="fas fa-crown"></i>
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="match-actions">
          <button
            className="match-action-btn edit"
            onClick={(e) => {
              e.stopPropagation();
              handleEditNotes(e);
            }}
            title="Edit Notes"
          >
            <i className="fas fa-edit"></i>
          </button>
          {(deleteMatch || deleteVGCMatch) && (
            <button
              className="match-action-btn delete"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteConfirm(true);
              }}
              title="Delete Match"
            >
              <i className="fas fa-trash"></i>
            </button>
          )}
        </div>

        
        {/* Keep existing modals */}
        {showEditNotesModal && editingGameIndex !== null && (
          <EditNotesModal
            isOpen={true}
            onClose={() => setShowEditNotesModal(false)}
            notes={games[editingGameIndex]?.notes || ''}
            onSave={handleSaveGameNotes}
            title={`Game ${editingGameIndex + 1} Notes`}
          />
        )}
        
        <ConfirmModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={() => handleDelete(_id)}
          title="Delete Match"
          message="Are you sure you want to delete this match? This action cannot be undone."
        />
        
        {/* Game Details Modal - keep existing implementation */}
        {showGameDetailsModal && (() => {
          const modalRoot = document.getElementById('modal-root');
          if (!modalRoot) {
            console.warn("modal-root element not found, creating it dynamically");
            const newModalRoot = document.createElement('div');
            newModalRoot.id = 'modal-root';
            document.body.appendChild(newModalRoot);
          }
          
          return ReactDOM.createPortal(
          <div className="notes-modal-backdrop" onClick={closeGameDetailsModal}>
            <div 
              className="notes-modal" 
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '700px' }}
            >
              <div className="notes-modal-header">
                <h3>Match Details</h3>
                <button className="notes-modal-close" onClick={closeGameDetailsModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              {/* Game Tabs for multi-game matches */}
              {games && games.length > 1 && (
                <div style={{
                  display: 'flex',
                  borderBottom: '2px solid var(--border-color)',
                  backgroundColor: 'var(--bg-secondary)',
                  padding: '0 20px',
                  gap: '10px'
                }}>
                  {games.map((game, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedGame({...game, gameNumber: index + 1})}
                      style={{
                        padding: '12px 20px',
                        border: 'none',
                        backgroundColor: selectedGame?.gameNumber === index + 1 ? 'var(--bg-primary)' : 'transparent',
                        color: selectedGame?.gameNumber === index + 1 ? 'var(--text-primary)' : 'var(--text-secondary)',
                        borderBottom: selectedGame?.gameNumber === index + 1 ? '2px solid var(--link-color)' : '2px solid transparent',
                        marginBottom: '-2px',
                        cursor: 'pointer',
                        fontWeight: selectedGame?.gameNumber === index + 1 ? 'bold' : 'normal',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedGame?.gameNumber !== index + 1) {
                          e.target.style.backgroundColor = 'var(--link-hover-bg)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedGame?.gameNumber !== index + 1) {
                          e.target.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <span>Game {index + 1}</span>
                      <span style={{
                        fontSize: '0.8rem',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: game.result === 'win' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                        color: game.result === 'win' ? '#4CAF50' : '#F44336'
                      }}>
                        {game.result === 'win' ? 'W' : 'L'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              
              <div className="notes-modal-body vgc-modal-body" style={{ 
                overflowY: 'auto', 
                padding: '20px',
                paddingBottom: '80px',
                display: 'block' 
              }}>
                {/* Only show game content if we have a selected game */}
                {selectedGame && (
                  <>
                {/* Rest of modal content remains the same */}
                {/* Game Result Section */}
                <div style={{ 
                  padding: '15px', 
                  marginBottom: '15px', 
                  borderRadius: '8px',
                  backgroundColor: selectedGame.result === 'win' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                  border: `1px solid ${selectedGame.result === 'win' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <h4 style={{ margin: '0 0 5px', fontSize: '1.2rem' }}>
                      {selectedGame.result === 'win' ? 'Victory' : 'Defeat'}
                    </h4>
                    <div>
                      {selectedGame.wentFirst ? 'You went first' : 'Opponent went first'}
                    </div>
                  </div>
                  <div style={{ 
                    fontSize: '1.8rem', 
                    fontWeight: 'bold',
                    color: selectedGame.result === 'win' ? '#4CAF50' : '#F44336' 
                  }}>
                    {selectedGame.result === 'win' ? '✓' : '✗'}
                  </div>
                </div>
                
                {/* Team Information - Full Teams */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '5px' }}>
                    <i className="fas fa-users"></i> Team Information (Full Teams)
                  </h4>
                  <div className="game-teams-visual" style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    margin: '10px 0',
                    padding: '15px 10px',
                    backgroundColor: 'var(--link-hover-bg)',
                    borderRadius: '5px',
                    gap: '10px'
                  }}>
                    {/* Your Full Team */}
                    <div className="player-team-section" style={{ textAlign: 'center', flex: 1 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '14px' }}>Your Full Team</div>
                      <div className="game-team-flex" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px' }}>
                        {parsedPlayerTeam && parsedPlayerTeam.length > 0 ? (
                          parsedPlayerTeam.map((pokemon, i) => (
                            <div key={`modal-pb-${i}`} style={{ margin: '2px', textAlign: 'center' }}>
                              <PokemonSprite 
                                pokemonName={pokemon}
                                style={{ width: '48px', height: '48px', display: 'block', margin: '0 auto' }}
                              />
                              <div style={{ fontSize: '12px', maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {pokemon.split('-')[0]}
                              </div>
                            </div>
                          ))
                        ) : gameTeamsMap[selectedGame.gameNumber - 1]?.playerTeamData ? (
                          gameTeamsMap[selectedGame.gameNumber - 1].playerTeamData.map((pokemon, i) => (
                            <div key={`modal-ps-${i}`} style={{ margin: '2px', textAlign: 'center' }}>
                              <PokemonSprite 
                                pokemonName={pokemon}
                                style={{ width: '48px', height: '48px', display: 'block', margin: '0 auto' }}
                              />
                              <div style={{ fontSize: '12px', maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {pokemon.split('-')[0]}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '0.8rem' }}>
                            No player team data available
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <span className="game-team-vs" style={{ margin: '0 10px', fontWeight: 'bold', alignSelf: 'center' }}>VS</span>
                    
                    {/* Opponent Full Team */}
                    <div className="opponent-team-section" style={{ textAlign: 'center', flex: 1 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '14px' }}>Opponent Full Team</div>
                      <div className="game-team-flex" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px' }}>
                        {parsedOpponentTeam && parsedOpponentTeam.length > 0 ? (
                          parsedOpponentTeam.map((pokemon, i) => (
                            <div key={`modal-ot-${i}`} style={{ margin: '2px', textAlign: 'center' }}>
                              <PokemonSprite 
                                pokemonName={pokemon}
                                style={{ width: '48px', height: '48px', display: 'block', margin: '0 auto' }}
                              />
                              <div style={{ fontSize: '12px', maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {pokemon.split('-')[0]}
                              </div>
                            </div>
                          ))
                        ) : selectedGame.opponentLeadPokemon && selectedGame.opponentLeadPokemon.length > 0 ? (
                          selectedGame.opponentLeadPokemon.map((pokemon, i) => (
                            <div key={`modal-ol-${i}`} style={{ margin: '2px', textAlign: 'center' }}>
                              <PokemonSprite 
                                pokemonName={pokemon}
                                style={{ width: '48px', height: '48px', display: 'block', margin: '0 auto' }}
                              />
                              <div style={{ fontSize: '12px', maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {pokemon.split('-')[0]}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '0.8rem' }}>
                            No opponent team data available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lead Information */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '5px' }}>
                    <i className="fas fa-chess-knight"></i> Lead Information
                  </h4>
                  <div className="lead-info-container" style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    gap: '15px', 
                    flexWrap: 'wrap',
                    backgroundColor: 'var(--link-hover-bg)',
                    padding: '12px',
                    borderRadius: '5px'
                  }}>
                    <div style={{ flex: 1, minWidth: '140px' }}>
                      <strong style={{ display: 'block', textAlign: 'center', marginBottom: '10px' }}>Your Leads:</strong>
                      <div style={{ display: 'flex', marginTop: '5px', justifyContent: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        {selectedGame.leadPokemon && selectedGame.leadPokemon.map((pokemon, i) => (
                          <div key={`lead-${i}`} style={{ textAlign: 'center' }}>
                            <PokemonSprite 
                              pokemonName={pokemon}
                              style={{ width: '40px', height: '40px' }}
                            />
                            <div style={{ fontSize: '12px', maxWidth: '60px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {pokemon.split('-')[0]}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{ flex: 1, minWidth: '140px' }}>
                      <strong style={{ display: 'block', textAlign: 'center', marginBottom: '10px' }}>Opponent Leads:</strong>
                      <div style={{ display: 'flex', marginTop: '5px', justifyContent: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        {selectedGame.opponentLeadPokemon && selectedGame.opponentLeadPokemon.map((pokemon, i) => (
                          <div key={`opp-lead-${i}`} style={{ textAlign: 'center' }}>
                            <PokemonSprite 
                              pokemonName={pokemon}
                              style={{ width: '40px', height: '40px' }}
                            />
                            <div style={{ fontSize: '12px', maxWidth: '60px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {pokemon.split('-')[0]}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Used Pokemon Section */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '5px' }}>
                    <i className="fas fa-fist-raised"></i> Pokemon Used in Battle
                  </h4>
                  <div className="used-pokemon-container" style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    margin: '10px 0',
                    padding: '15px 10px',
                    backgroundColor: 'var(--link-hover-bg)',
                    borderRadius: '5px',
                    gap: '10px'
                  }}>
                    {/* Your Used Pokemon */}
                    <div className="player-used-section" style={{ textAlign: 'center', flex: 1 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '14px', color: '#4CAF50' }}>Your Used Pokemon</div>
                      <div className="used-pokemon-flex" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px' }}>
                        {selectedGame.broughtPokemon && selectedGame.broughtPokemon.length > 0 ? (
                          selectedGame.broughtPokemon.map((pokemon, i) => (
                            <div key={`used-p-${i}`} style={{ margin: '2px', textAlign: 'center' }}>
                              <PokemonSprite 
                                pokemonName={pokemon}
                                style={{ width: '56px', height: '56px', display: 'block', margin: '0 auto' }}
                              />
                              <div style={{ fontSize: '13px', maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: '500' }}>
                                {pokemon.split('-')[0]}
                              </div>
                            </div>
                          ))
                        ) : gameTeamsMap[selectedGame.gameNumber - 1]?.playerTeamData && gameTeamsMap[selectedGame.gameNumber - 1].playerTeamData.length === 4 ? (
                          gameTeamsMap[selectedGame.gameNumber - 1].playerTeamData.map((pokemon, i) => (
                            <div key={`used-pt-${i}`} style={{ margin: '2px', textAlign: 'center' }}>
                              <PokemonSprite 
                                pokemonName={pokemon}
                                style={{ width: '56px', height: '56px', display: 'block', margin: '0 auto' }}
                              />
                              <div style={{ fontSize: '13px', maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: '500' }}>
                                {pokemon.split('-')[0]}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '0.8rem' }}>
                            No data for used Pokemon
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div style={{ width: '1px', backgroundColor: 'var(--border-color)', alignSelf: 'stretch', margin: '0 10px' }}></div>
                    
                    {/* Opponent Used Pokemon */}
                    <div className="opponent-used-section" style={{ textAlign: 'center', flex: 1 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '14px', color: '#F44336' }}>Opponent Used Pokemon</div>
                      <div className="used-pokemon-flex" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px' }}>
                        {selectedGame.opponentBroughtPokemon && selectedGame.opponentBroughtPokemon.length > 0 ? (
                          selectedGame.opponentBroughtPokemon.map((pokemon, i) => (
                            <div key={`used-o-${i}`} style={{ margin: '2px', textAlign: 'center' }}>
                              <PokemonSprite 
                                pokemonName={pokemon}
                                style={{ width: '56px', height: '56px', display: 'block', margin: '0 auto' }}
                              />
                              <div style={{ fontSize: '13px', maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: '500' }}>
                                {pokemon.split('-')[0]}
                              </div>
                            </div>
                          ))
                        ) : gameTeamsMap[selectedGame.gameNumber - 1]?.opponentTeamData && gameTeamsMap[selectedGame.gameNumber - 1].opponentTeamData.length === 4 ? (
                          gameTeamsMap[selectedGame.gameNumber - 1].opponentTeamData.map((pokemon, i) => (
                            <div key={`used-ot-${i}`} style={{ margin: '2px', textAlign: 'center' }}>
                              <PokemonSprite 
                                pokemonName={pokemon}
                                style={{ width: '56px', height: '56px', display: 'block', margin: '0 auto' }}
                              />
                              <div style={{ fontSize: '13px', maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: '500' }}>
                                {pokemon.split('-')[0]}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '0.8rem' }}>
                            No data for used Pokemon
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Turn Order and First Knockout */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '5px' }}>
                    <i className="fas fa-hourglass-half"></i> Battle Information
                  </h4>
                  <div style={{ fontSize: '0.9rem', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <div><strong>Turn Order:</strong> {selectedGame.wentFirst ? 'You went first' : 'Opponent went first'}</div>
                    {selectedGame.firstKnockout && (
                      <div><strong>First Knock Out By:</strong> {selectedGame.firstKnockout}</div>
                    )}
                    {selectedGame.replayLink && (
                      <div style={{ marginTop: '10px' }}>
                        <a
                          href={selectedGame.replayLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="replay-link"
                          style={{ display: 'inline-flex', alignItems: 'center' }}
                        >
                          <i className="fas fa-play-circle" style={{ marginRight: '5px' }} />
                          Watch Battle Replay
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Game Notes */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '5px' }}>
                    <i className="far fa-sticky-note"></i> Game Notes
                  </h4>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div>Record your thoughts about this game</div>
                    <button 
                      className="btn notes-edit-button" 
                      onClick={(e) => {
                        e.stopPropagation();
                        closeGameDetailsModal();
                        handleEditGameNotes(e, selectedGame.gameNumber - 1);
                      }}
                      type="button"
                      style={{ marginLeft: 'auto' }}
                    >
                      <i className="far fa-edit"></i>
                      {selectedGame.notes ? 'Edit Notes' : 'Add Notes'}
                    </button>
                  </div>
                  {selectedGame.notes ? (
                    <div style={{ 
                      whiteSpace: 'pre-wrap',
                      backgroundColor: 'var(--link-hover-bg)',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid var(--border-color)'
                    }}>
                      {selectedGame.notes}
                    </div>
                  ) : (
                    <div style={{ 
                      backgroundColor: 'var(--link-hover-bg)',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-secondary)',
                      fontStyle: 'italic'
                    }}>
                      No notes added yet. Click "Add Notes" to record your observations.
                    </div>
                  )}
                </div>
                  </>
                )}
              </div>
              
              <div className="notes-modal-footer">
                <button className="btn-cancel" onClick={closeGameDetailsModal}>Close</button>
              </div>
            </div>
          </div>,
          document.getElementById('modal-root') || document.body);
        })()}
      </div>
    );
  }

  // Original UI
  return (
    <div
      className="match-item"
      onClick={() => {
        if (games && games.length > 0) {
          setSelectedGame({...games[0], gameNumber: 1});
        } else {
          setSelectedGame({ gameNumber: 1 });
        }
        setShowGameDetailsModal(true);
      }}
      style={{ borderLeftColor: `var(--${matchResult.status}-color)` }}
    >
      {/* Collapsed View */}
      <div className="match-header">
        <div>
          <span className={`badge badge-${matchResult.status}`}>
            {matchResult.result} {score}
          </span>
          vs {opponentName || opponentDeck || 'Unknown'}
          {group && (
            <span className="group-badge" style={{ marginLeft: '0.5rem' }}>
              <i className="fas fa-object-group"></i> {group.name}
            </span>
          )}
        </div>
      </div>

      <div className="vgc-teams-visual">
        <div className="team-flex">
          {playerSprites.map((url, i) => (
            <div key={`ps-${i}`} className="pokemon-sprite-container">
              <img src={url} alt="" />
              {isRestricted(parsedPlayerTeam[i], 'player') && (
                <span className="restricted-indicator" title="Restricted Pokémon">
                  <i className="fas fa-crown"></i>
                </span>
              )}
            </div>
          ))}
        </div>
        <span className="team-vs">VS</span>
        <div className="team-flex">
          {opponentSprites.map((url, i) => (
            <div key={`os-${i}`} className="pokemon-sprite-container">
              <img src={url} alt="" />
              {isRestricted(parsedOpponentTeam[i], 'opponent') && (
                <span className="restricted-indicator" title="Restricted Pokémon">
                  <i className="fas fa-crown"></i>
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Expanded View - Removed, now opens modal directly */}
      {false && (
        <>
          <div className="match-metadata">
            <div className="match-date">
              <i className="far fa-calendar-alt" /> {formatDate(date || createdAt)}
            </div>
            <div className="match-format">
              <i className="fas fa-trophy" /> {format || `Best of ${bestOf}`}
            </div>
            {opponentName && (
              <div className="match-opponent">
                <i className="fas fa-user" /> {opponentName}
              </div>
            )}
            {/* Replay link moved to individual games and bottom of expanded view */}
          </div>

          {games?.map((game, index) => (
            <div
              key={index}
              className={`game-item ${game.result === 'win' ? 'game-win' : 'game-loss'}`}
              onClick={(e) => handleGameClick(e, game, index)}
              style={{ cursor: 'pointer' }}
            >
              <div className="game-header">
                <div>Game {index + 1} — {game.result === 'win' ? 'Won' : 'Lost'}</div>
                <span style={{ 
                  fontSize: '0.7rem', 
                  color: 'var(--link-active-color)',
                  backgroundColor: 'var(--link-hover-bg)',
                  padding: '3px 8px',
                  borderRadius: '4px',
                  display: 'inline-flex',
                  alignItems: 'center'
                }}>
                  <i className="fas fa-search-plus" style={{ marginRight: '4px' }}></i> View Details
                </span>
              </div>

              {/* Simplified game summary */}
              <div className="game-summary">
                {/* Just show the leads as a minimal summary */}
                <div className="leads-mini-display" style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  margin: '10px 0',
                  padding: '10px',
                  backgroundColor: 'var(--link-hover-bg)',
                  borderRadius: '5px',
                  flexWrap: 'wrap',
                  gap: '10px'
                }}>
                  <div className="leads-mini-column" style={{ textAlign: 'center', flex: 1, minWidth: '130px' }}>
                    <div style={{ fontSize: '12px', marginBottom: '8px', fontWeight: 'bold' }}>Your Leads</div>
                    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '5px' }}>
                      {(gameLeadSprites[index] || []).map((url, i) => (
                        <img
                          key={`you-lead-${i}`}
                          src={url}
                          alt="Your Lead"
                          style={{ width: '32px', height: '32px' }}
                        />
                      ))}
                    </div>
                  </div>
                  <span className="vs-divider" style={{ margin: '0 5px', fontSize: '12px', alignSelf: 'center' }}>VS</span>
                  <div className="leads-mini-column" style={{ textAlign: 'center', flex: 1, minWidth: '130px' }}>
                    <div style={{ fontSize: '12px', marginBottom: '8px', fontWeight: 'bold' }}>Opponent Leads</div>
                    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '5px' }}>
                      {(gameOpponentLeadSprites[index] || []).map((url, i) => (
                        <img
                          key={`opp-lead-${i}`}
                          src={url}
                          alt="Opponent Lead"
                          style={{ width: '32px', height: '32px' }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Game replay link - keep this for easy access */}
                {game.replayLink && (
                  <div className="game-replay-link" style={{ margin: '10px 0', textAlign: 'center' }}>
                    <a
                      href={game.replayLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="replay-link"
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        display: 'inline-block',
                        padding: '5px 15px',
                        backgroundColor: 'var(--link-hover-bg)',
                        borderRadius: '4px',
                        color: 'var(--link-active-color)',
                        textDecoration: 'none',
                        fontWeight: 'bold'
                      }}
                    >
                      <i className="fas fa-play-circle" style={{ marginRight: '5px' }} />
                      Watch Replay
                    </a>
                  </div>
                )}

                {/* Show just a preview of notes if they exist */}
                {game.notes && (
                  <div className="game-notes-preview" style={{ 
                    margin: '10px 0', 
                    fontSize: '0.85rem',
                    color: 'var(--text-secondary)',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    <i className="far fa-sticky-note" style={{ marginRight: '5px' }}></i>
                    {game.notes.length > 40 
                      ? `${game.notes.substring(0, 40)}...` 
                      : game.notes}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Match-level Replay Link - Only show if it's not already shown in games */}
          {replayLink && games.length === 0 && (
            <div className="match-replay-link">
              <a
                href={replayLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="replay-link"
              >
                <i className="fas fa-play-circle" /> Watch Match Replay
              </a>
            </div>
          )}
          
          {/* Match-level notes removed - only using game-specific notes now */}
        </>
      )}

      {/* Format badge and delete button container */}
      <div style={{
        position: 'absolute',
        top: '0.5rem',
        right: '0.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <span className="badge badge-secondary" style={{ fontSize: '0.75rem' }}>
          Doubles
        </span>
        {(deleteMatch || deleteVGCMatch) && (
          <button
            className="btn btn-danger btn-sm"
            style={{
              padding: '0.25rem 0.5rem',
              fontSize: '0.75rem',
              borderRadius: '999px',
              display: 'inline-flex',
              alignItems: 'center'
            }}
            onClick={(e) => {
              e.stopPropagation(); // Prevent toggle on click
              setShowDeleteConfirm(true);
            }}
          >
            <i className="fas fa-trash-alt" style={{ marginRight: '0.25rem' }} />
            Delete
          </button>
        )}
      </div>
      
      {/* Game Notes Edit Modal */}
      {showEditNotesModal && editingGameIndex !== null && (
        <EditNotesModal
          isOpen={true}
          onClose={() => setShowEditNotesModal(false)}
          notes={games[editingGameIndex]?.notes || ''}
          onSave={handleSaveGameNotes}
          title={`Game ${editingGameIndex + 1} Notes`}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => handleDelete(_id)}
        title="Delete Match"
        message="Are you sure you want to delete this match? This action cannot be undone."
      />
      
      {/* Game Details Modal */}
      {showGameDetailsModal && selectedGame && (() => {
        // Try to find the modal-root element
        const modalRoot = document.getElementById('modal-root');
        
        // If modal-root doesn't exist, create it dynamically
        if (!modalRoot) {
          console.warn("modal-root element not found, creating it dynamically");
          const newModalRoot = document.createElement('div');
          newModalRoot.id = 'modal-root';
          document.body.appendChild(newModalRoot);
        }
        
        return ReactDOM.createPortal(
        <div className="notes-modal-backdrop" onClick={closeGameDetailsModal}>
          <div 
            className="notes-modal" 
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '700px' }}
          >
            <div className="notes-modal-header">
              <h3>Match Details</h3>
              <button className="notes-modal-close" onClick={closeGameDetailsModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            {/* Game Tabs for multi-game matches */}
            {games && games.length > 1 && (
              <div style={{
                display: 'flex',
                borderBottom: '2px solid var(--border-color)',
                backgroundColor: 'var(--bg-secondary)',
                padding: '0 20px',
                gap: '10px'
              }}>
                {games.map((game, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedGame({...game, gameNumber: index + 1})}
                    style={{
                      padding: '12px 20px',
                      border: 'none',
                      backgroundColor: selectedGame?.gameNumber === index + 1 ? 'var(--bg-primary)' : 'transparent',
                      color: selectedGame?.gameNumber === index + 1 ? 'var(--text-primary)' : 'var(--text-secondary)',
                      borderBottom: selectedGame?.gameNumber === index + 1 ? '2px solid var(--link-color)' : '2px solid transparent',
                      marginBottom: '-2px',
                      cursor: 'pointer',
                      fontWeight: selectedGame?.gameNumber === index + 1 ? 'bold' : 'normal',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedGame?.gameNumber !== index + 1) {
                        e.target.style.backgroundColor = 'var(--link-hover-bg)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedGame?.gameNumber !== index + 1) {
                        e.target.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <span>Game {index + 1}</span>
                    <span style={{
                      fontSize: '0.8rem',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      backgroundColor: game.result === 'win' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                      color: game.result === 'win' ? '#4CAF50' : '#F44336'
                    }}>
                      {game.result === 'win' ? 'W' : 'L'}
                    </span>
                  </button>
                ))}
              </div>
            )}
            
            <div className="notes-modal-body vgc-modal-body" style={{ 
              overflowY: 'auto', 
              padding: '20px',
              paddingBottom: '80px', /* Add extra padding at bottom for better mobile scrolling */
              display: 'block' 
            }}>
              {/* Only show game content if we have a selected game */}
              {selectedGame && (
                <>
              {/* Game Result Section */}
              <div style={{ 
                padding: '15px', 
                marginBottom: '15px', 
                borderRadius: '8px',
                backgroundColor: selectedGame.result === 'win' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                border: `1px solid ${selectedGame.result === 'win' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h4 style={{ margin: '0 0 5px', fontSize: '1.2rem' }}>
                    {selectedGame.result === 'win' ? 'Victory' : 'Defeat'}
                  </h4>
                  <div>
                    {selectedGame.wentFirst ? 'You went first' : 'Opponent went first'}
                  </div>
                </div>
                <div style={{ 
                  fontSize: '1.8rem', 
                  fontWeight: 'bold',
                  color: selectedGame.result === 'win' ? '#4CAF50' : '#F44336' 
                }}>
                  {selectedGame.result === 'win' ? '✓' : '✗'}
                </div>
              </div>
              
              {/* Team Information */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 10px', fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '5px' }}>
                  <i className="fas fa-users"></i> Team Information
                </h4>
                <div className="game-teams-visual" style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  margin: '10px 0',
                  padding: '15px 10px',
                  backgroundColor: 'var(--link-hover-bg)',
                  borderRadius: '5px',
                  gap: '10px'
                }}>
                  {/* Your Team */}
                  <div className="player-team-section" style={{ textAlign: 'center', flex: 1 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '14px' }}>Your Team</div>
                    <div className="game-team-flex" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px' }}>
                      {selectedGame.broughtPokemon && selectedGame.broughtPokemon.length > 0 ? (
                        selectedGame.broughtPokemon.map((pokemon, i) => (
                          <div key={`modal-pb-${i}`} style={{ margin: '2px', textAlign: 'center' }}>
                            <PokemonSprite 
                              pokemonName={pokemon}
                              style={{ width: '48px', height: '48px', display: 'block', margin: '0 auto' }}
                            />
                            <div style={{ fontSize: '12px', maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {pokemon.split('-')[0]}
                            </div>
                          </div>
                        ))
                      ) : gameTeamsMap[selectedGame.gameNumber - 1]?.playerTeamData ? (
                        gameTeamsMap[selectedGame.gameNumber - 1].playerTeamData.map((pokemon, i) => (
                          <div key={`modal-ps-${i}`} style={{ margin: '2px', textAlign: 'center' }}>
                            <PokemonSprite 
                              pokemonName={pokemon}
                              style={{ width: '48px', height: '48px', display: 'block', margin: '0 auto' }}
                            />
                            <div style={{ fontSize: '12px', maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {pokemon.split('-')[0]}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '0.8rem' }}>
                          No player team data available
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <span className="game-team-vs" style={{ margin: '0 10px', fontWeight: 'bold', alignSelf: 'center' }}>VS</span>
                  
                  {/* Opponent Team */}
                  <div className="opponent-team-section" style={{ textAlign: 'center', flex: 1 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '14px' }}>Opponent Team</div>
                    <div className="game-team-flex" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px' }}>
                      {/* Prioritize stored opponent brought Pokemon from database */}
                      {selectedGame.opponentBroughtPokemon && selectedGame.opponentBroughtPokemon.length > 0 ? (
                        selectedGame.opponentBroughtPokemon.map((pokemon, i) => (
                          <div key={`modal-ob-${i}`} style={{ margin: '2px', textAlign: 'center' }}>
                            <PokemonSprite 
                              pokemonName={pokemon}
                              style={{ width: '48px', height: '48px', display: 'block', margin: '0 auto' }}
                            />
                            <div style={{ fontSize: '12px', maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {pokemon.split('-')[0]}
                            </div>
                          </div>
                        ))
                      ) : gameTeamsMap[selectedGame.gameNumber - 1]?.opponentTeamData && 
                       gameTeamsMap[selectedGame.gameNumber - 1].opponentTeamData.length > 0 ? (
                        gameTeamsMap[selectedGame.gameNumber - 1].opponentTeamData.map((pokemon, i) => (
                          <div key={`modal-os-${i}`} style={{ margin: '2px', textAlign: 'center' }}>
                            <PokemonSprite 
                              pokemonName={pokemon}
                              style={{ width: '48px', height: '48px', display: 'block', margin: '0 auto' }}
                            />
                            <div style={{ fontSize: '12px', maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {pokemon.split('-')[0]}
                            </div>
                          </div>
                        ))
                      ) : parsedOpponentTeam && parsedOpponentTeam.length > 0 ? (
                        // Fall back to using parsedOpponentTeam if no sprites available
                        parsedOpponentTeam.map((pokemon, i) => (
                          <div key={`modal-ot-${i}`} style={{ margin: '2px', textAlign: 'center' }}>
                            <PokemonSprite 
                              pokemonName={pokemon}
                              style={{ width: '48px', height: '48px', display: 'block', margin: '0 auto' }}
                            />
                            <div style={{ fontSize: '12px', maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {pokemon.split('-')[0]}
                            </div>
                          </div>
                        ))
                      ) : selectedGame.opponentLeadPokemon && selectedGame.opponentLeadPokemon.length > 0 ? (
                        // Last resort: just show the leads if nothing else is available
                        selectedGame.opponentLeadPokemon.map((pokemon, i) => (
                          <div key={`modal-ol-${i}`} style={{ margin: '2px', textAlign: 'center' }}>
                            <PokemonSprite 
                              pokemonName={pokemon}
                              style={{ width: '48px', height: '48px', display: 'block', margin: '0 auto' }}
                            />
                            <div style={{ fontSize: '12px', maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {pokemon.split('-')[0]}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '0.8rem' }}>
                          No opponent team data available
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Lead Information */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 10px', fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '5px' }}>
                  <i className="fas fa-chess-knight"></i> Lead Information
                </h4>
                <div className="lead-info-container" style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  gap: '15px', 
                  flexWrap: 'wrap',
                  backgroundColor: 'var(--link-hover-bg)',
                  padding: '12px',
                  borderRadius: '5px'
                }}>
                  <div style={{ flex: 1, minWidth: '140px' }}>
                    <strong style={{ display: 'block', textAlign: 'center', marginBottom: '10px' }}>Your Leads:</strong>
                    <div style={{ display: 'flex', marginTop: '5px', justifyContent: 'center', flexWrap: 'wrap', gap: '10px' }}>
                      {selectedGame.leadPokemon && selectedGame.leadPokemon.map((pokemon, i) => (
                        <div key={`lead-${i}`} style={{ textAlign: 'center' }}>
                          <PokemonSprite 
                            pokemonName={pokemon}
                            style={{ width: '40px', height: '40px' }}
                          />
                          <div style={{ fontSize: '12px', maxWidth: '60px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {pokemon.split('-')[0]}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: '140px' }}>
                    <strong style={{ display: 'block', textAlign: 'center', marginBottom: '10px' }}>Opponent Leads:</strong>
                    <div style={{ display: 'flex', marginTop: '5px', justifyContent: 'center', flexWrap: 'wrap', gap: '10px' }}>
                      {selectedGame.opponentLeadPokemon && selectedGame.opponentLeadPokemon.map((pokemon, i) => (
                        <div key={`opp-lead-${i}`} style={{ textAlign: 'center' }}>
                          <PokemonSprite 
                            pokemonName={pokemon}
                            style={{ width: '40px', height: '40px' }}
                          />
                          <div style={{ fontSize: '12px', maxWidth: '60px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {pokemon.split('-')[0]}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Turn Order and First Knockout */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 10px', fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '5px' }}>
                  <i className="fas fa-hourglass-half"></i> Battle Information
                </h4>
                <div style={{ fontSize: '0.9rem', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <div><strong>Turn Order:</strong> {selectedGame.wentFirst ? 'You went first' : 'Opponent went first'}</div>
                  {selectedGame.firstKnockout && (
                    <div><strong>First Knock Out By:</strong> {selectedGame.firstKnockout}</div>
                  )}
                  {selectedGame.replayLink && (
                    <div style={{ marginTop: '10px' }}>
                      <a
                        href={selectedGame.replayLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="replay-link"
                        style={{ display: 'inline-flex', alignItems: 'center' }}
                      >
                        <i className="fas fa-play-circle" style={{ marginRight: '5px' }} />
                        Watch Battle Replay
                      </a>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Game Notes */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 10px', fontSize: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '5px' }}>
                  <i className="far fa-sticky-note"></i> Game Notes
                </h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div>Record your thoughts about this game</div>
                  <button 
                    className="btn notes-edit-button" 
                    onClick={(e) => {
                      e.stopPropagation();
                      closeGameDetailsModal();
                      handleEditGameNotes(e, selectedGame.gameNumber - 1);
                    }}
                    type="button"
                    style={{ marginLeft: 'auto' }}
                  >
                    <i className="far fa-edit"></i>
                    {selectedGame.notes ? 'Edit Notes' : 'Add Notes'}
                  </button>
                </div>
                {selectedGame.notes ? (
                  <div style={{ 
                    whiteSpace: 'pre-wrap',
                    backgroundColor: 'var(--link-hover-bg)',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)'
                  }}>
                    {selectedGame.notes}
                  </div>
                ) : (
                  <div style={{ 
                    backgroundColor: 'var(--link-hover-bg)',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)',
                    color: 'var(--text-secondary)',
                    fontStyle: 'italic'
                  }}>
                    No notes added yet. Click "Add Notes" to record your observations.
                  </div>
                )}
              </div>
                </>
              )}
            </div>
            
            <div className="notes-modal-footer">
              <button className="btn-cancel" onClick={closeGameDetailsModal}>Close</button>
            </div>
          </div>
        </div>,
      document.getElementById('modal-root') || document.body);
      })()}
    </div>
  );
};

export default MatchItemVGC;